/* tslint:disable:max-file-line-count */
import { autorun } from 'mobx';
import { inject, observer } from 'mobx-react';
import * as React from 'react';

import { AbacusControl, AbacusExperiment, AbacusVariant } from 'bernie-abacus';
import { AnalyticsStore, ContextStore, ExperimentStore } from 'bernie-core';
import { ErrorBoundary } from 'bernie-view';
import { LocalizedComponentProps, withLocalization } from 'bernie-l10n';
import { ViewMedium, Viewport, ViewSmall } from 'bernie-view';

import { CurrentHotelStore } from 'stores/current-hotel-store';
import { SearchResultsStore } from 'stores/search-results-store';
import { ShortlistStore } from 'stores/shortlist-store';

import { classNames } from 'uitk-react-utils';
import { ToolbarType, UitkToolbar } from 'uitk-react-toolbar';
import { UitkFullscreenDialog } from 'uitk-react-dialog';
import { UitkMap } from 'uitk-react-map';
import { UitkTertiaryButton } from 'uitk-react-button';

import { ClusteringMarkers } from 'components/common/map/markers/clustering-markers';
import { DynamicSheet } from './dynamic-sheet';
import { FiltersToggle } from 'components/search/sort-filter/filters-toggle';
import { Listing } from 'components/search/listing';
import { MapSearch } from 'components/search/map/map-search';
import { MessagingSheet } from 'components/search/map/messaging-sheet';

export interface SearchResultsMapProps {
  analytics?: AnalyticsStore;
  context?: ContextStore;
  currentHotel?: CurrentHotelStore;
  experiment?: ExperimentStore;
  onDismiss?: () => void;
  routeStore?: any;
  searchResults?: SearchResultsStore;
  shortlist?: ShortlistStore;
  splitView?: boolean;
}

export interface SearchResultsMapState {
  center?: any;
  markerSelected?: boolean;
  searchChanged?: boolean;
  selectedHotel: any;
  selectedHotelId?: any;
  selectedMarker: any;
  singleMarkerChanged?: boolean;
  zoom?: number;
}
export type LocalizedSearchResultsMapProps = LocalizedComponentProps & SearchResultsMapProps;

@withLocalization
@inject('analytics', 'context', 'currentHotel', 'experiment', 'routeStore', 'searchResults', 'shortlist')
@observer
export class SearchResultsMap extends React.Component<LocalizedSearchResultsMapProps, SearchResultsMapState> {
  private zIndex: number;
  private cancelHotelWatch: any;
  private cancelShortlistWatch: any;
  private trackMapPan: () => void;
  private trackMapZoomIn: () => void;
  private trackMapZoomOut: () => void;
  private trackMarkerSelect: () => void;
  private trackClusterDisplayed: () => void;
  private trackClusterSelect: () => void;

  public constructor(props) {
    super(props);
    this.setUpAnalyticsTrackers(props.analytics);
    this.dismissSheet = this.dismissSheet.bind(this);
    this.onFavoriteClick = this.onFavoriteClick.bind(this);
    this.onMapPan = this.onMapPan.bind(this);
    this.onMarkerSelect = this.onMarkerSelect.bind(this);
    this.onMarkersUpdate = this.onMarkersUpdate.bind(this);
    this.onSingleMarkerUpdate = this.onSingleMarkerUpdate.bind(this);
    this.zIndex =
      props.searchResults.map && props.searchResults.map.markers && props.searchResults.map.markers.length
        ? props.searchResults.hotels.length
        : 0;
    this.state = {
      markerSelected: false,
      searchChanged: false,
      selectedHotel: undefined,
      selectedMarker: null,
      singleMarkerChanged: false
    };
  }

  private setUpAnalyticsTrackers(analytics) {
    const noop = () => null;
    const clickTracker = analytics && analytics.clickTracker ? analytics.clickTracker.bind(analytics) : noop;
    this.trackMapPan = clickTracker('Module.Map.Pan', 'User panning PWA HSR Map', null, noop);
    this.trackMapZoomIn = clickTracker('Module.Map.Zoom.In', 'User zooming in on PWA HSR Map', null, noop);
    this.trackMapZoomOut = clickTracker('Module.Map.Zoom.Out', 'User zooming out on PWA HSR Map', null, noop);
    this.trackMarkerSelect = clickTracker('Module.Map.HotelSelect', 'User selecting a pin on the PWA HSR map', null, noop);
    this.trackClusterDisplayed = clickTracker(
      'Module.Map.HotelClusterDisplayed',
      'User has seen at least one cluster on the PWA HSR map',
      null,
      noop
    );
    this.trackClusterSelect = clickTracker('Module.Map.HotelClusterSelect', 'User selecting a cluster on the PWA HSR map', null, noop);
  }

  public componentDidMount() {
    const { routeStore, shortlist, searchResults, analytics } = this.props;

    this.cancelHotelWatch = autorun(() => {
      if (routeStore.state && routeStore.state.selectedHotelId && routeStore.state.selectedHotelId !== this.state.selectedHotel) {
        this.setState({
          selectedHotel: searchResults.findHotel(routeStore.state.selectedHotelId),
          selectedMarker: this.findSelectedHotel(routeStore.state.selectedHotelId)
        });
      }
      if (searchResults.hotels && searchResults.hotels.length > 0) {
        this.setState({
          searchChanged: true,
          selectedHotel: undefined,
          selectedMarker: null,
          markerSelected: false
        });
      }
    });

    this.cancelShortlistWatch = autorun(() => {
      if (!shortlist.isLoading && Object.keys(shortlist.hotel.favoritedLocally).length === 0) {
        this.setState({
          searchChanged: true
        });
      }
    });

    const mapFilters =
      searchResults && searchResults.map && searchResults.map.filterDisplayOptions && searchResults.map.filterDisplayOptions.filters;
    if (mapFilters && analytics && typeof analytics.trackEvent === 'function') {
      analytics.trackEvent('.MapFilters.Imp', 'Showing map filters', analytics.omnitureData);
    }
  }

  public componentWillUnmount() {
    if (this.cancelHotelWatch) {
      this.cancelHotelWatch();
      this.cancelHotelWatch = undefined;
    }
    if (this.cancelShortlistWatch) {
      this.cancelShortlistWatch();
      this.cancelShortlistWatch = undefined;
    }
  }

  public deDuplicateMarkers(markers) {
    const hotelIds = {};
    if (!markers || !markers.length) {
      return [];
    }

    return markers.filter(marker => {
      const sheet = marker && marker.sheet;
      const hotel = marker && marker.hotel;
      const id = sheet ? sheet.id : hotel && hotel.id;
      const isUnique = id && !hotelIds[id];
      if (id) {
        hotelIds[id] = true;
      }

      return isUnique;
    });
  }

  public render() {
    const { context, searchResults, splitView, shortlist } = this.props;
    const { markerSelected, searchChanged, selectedHotelId, singleMarkerChanged } = this.state;
    const mapModel = searchResults && searchResults.map;
    const mapFilters = mapModel && mapModel.filterDisplayOptions && mapModel.filterDisplayOptions.filters;

    const toolBarAttributes = {
      type: ToolbarType.CLOSE,
      header: searchResults.searchedDestination,
      subtext: mapModel && mapModel.subtitle,
      iconLabel: this.props.l10n.formatText('maps.closeButton.label')
    };
    const toolbarAnalytics = this.props.analytics.setupTracker('.Module.Map.Link', 'Close Map Dialog');
    const dialogProps = {
      dialogShow: true,
      strictFullscreen: true
    };
    const mapProps = {
      ...mapModel,
      locale: context.locale,
      onPan: this.onMapPan,
      onZoomIn: this.trackMapZoomIn,
      onZoomOut: this.trackMapZoomOut,
      pageName: 'HotelSearch',
      clickablePOIs: false
    };
    const clusteringMarkersProps = {
      markers: mapModel && mapModel.markers ? this.deDuplicateMarkers(mapModel.markers) : [],
      markerSelected,
      experiment: this.props.experiment,
      onClusterDraw: this.trackClusterDisplayed,
      onClusterSelect: this.trackClusterSelect,
      onMapClick: this.dismissSheet,
      onSelect: this.onMarkerSelect,
      onSingleMarkerUpdate: this.onSingleMarkerUpdate,
      onMarkersUpdate: this.onMarkersUpdate,
      withClustering: true,
      searchChanged,
      singleMarkerChanged,
      hotelIdFromMap: selectedHotelId
    };

    const showSheet = this.state.selectedHotel && this.state.selectedMarker && this.state.selectedMarker.sheet;
    const mapClasses = classNames('uitk-field', { 'map-sticky': splitView });

    const mapSearchProps = {
      l10n: this.props.l10n,
      mapCenter: this.state.center,
      selectedHotel: this.state.selectedHotel,
      selectedMarker: this.state.selectedMarker,
      onFavoriteClick: this.onFavoriteClick,
      mapModel,
      showSheet,
      searchResults,
      mapProps,
      clusteringMarkersProps,
      splitView,
      shortlist
    };

    return (
      <>
        {this.props.splitView && (
          <div className={mapClasses}>
            <MapSearchWithMarkers {...mapSearchProps} />
          </div>
        )}
        {!this.props.splitView && (
          <UitkFullscreenDialog {...dialogProps}>
            <UitkToolbar
              {...toolBarAttributes}
              action={this.props.onDismiss}
              analytics={toolbarAnalytics}
              actionContent={
                mapFilters && (
                  <FiltersToggle
                    buttonComponent={UitkTertiaryButton}
                    isActive={true}
                    isFullWidth={false}
                    label="hotels.searchResults.openMapFiltersDialog.label"
                    a11yMessage="hotels.searchResults.appliedFiltersCount.a11yLabel"
                    rfrr=":MapFilters"
                    stid="open-map-filters"
                    filters={mapFilters}
                    dialogParam="map-filter"
                    dialogTitle={this.props.l10n.formatText('hotels.searchResults.openMapFiltersDialog.label')}
                  />
                )
              }
            />
            <MapSearchWithMarkers {...mapSearchProps} />
          </UitkFullscreenDialog>
        )}
      </>
    );
  }

  private onFavoriteClick() {
    this.setState({ singleMarkerChanged: true });
  }

  public onMapPan(center, zoom) {
    this.props.searchResults.displaySearchButton();
    this.setState({ center, zoom });
    this.trackMapPan();
  }

  private onMarkerSelect(marker) {
    this.setState({
      selectedHotelId: marker.sheet ? marker.sheet.id : marker.hotel.hotelId
    });
    this.trackMarkerSelect();
    this.selectMarker(marker);
  }

  private onSingleMarkerUpdate() {
    this.setState({ singleMarkerChanged: false });
  }

  private onMarkersUpdate() {
    this.setState({ searchChanged: false });
  }

  private selectMarker(marker) {
    if (this.state.selectedMarker && typeof this.state.selectedMarker.blur === 'function') {
      this.state.selectedMarker.blur();
    }
    this.zIndex += 1;
    if (marker && typeof marker.focus === 'function') {
      marker.focus(this.zIndex);
    }
    this.setState({
      markerSelected: true,
      selectedMarker: marker,
      selectedHotel: this.findSelectedHotelGivenMarker(marker)
    });
  }

  private findSelectedHotelGivenMarker(marker) {
    if (marker && marker.sheet) {
      const foundHotel = marker.sheet.id && this.props.searchResults.findHotel(marker.sheet.id.toString());
      if (foundHotel) {
        return foundHotel;
      }

      return marker.sheet;
    }

    return marker.hotel;
  }

  private findSelectedHotel(hotelId) {
    if (!hotelId) {
      return undefined;
    }
    const hotels = this.props.searchResults.map.markers;
    const hotelContainer = hotels && hotels.find(h => h.id && h.id === hotelId);

    return (hotelContainer && hotelContainer.hotel) || null;
  }

  private dismissSheet() {
    const { selectedMarker } = this.state;
    if (selectedMarker && typeof selectedMarker.blur === 'function') {
      selectedMarker.blur();
    }
    this.setState({
      markerSelected: false
    });
  }
}

interface MapSearchWithMarkersProps {
  mapModel: any;
  showSheet: boolean;
  searchResults?: SearchResultsStore;
  mapProps: any;
  clusteringMarkersProps: any;
  l10n: any;
  mapCenter?: any;
  splitView?: boolean;
  selectedHotel: any;
  selectedMarker: any;
  onFavoriteClick: any;
  shortlist?: any;
  mapCardTreatment?: boolean;
}

const mapSearchWithMarkers: React.FunctionComponent<MapSearchWithMarkersProps> = ({
  mapModel,
  showSheet,
  searchResults,
  mapProps,
  clusteringMarkersProps,
  l10n,
  mapCenter,
  splitView,
  selectedHotel,
  selectedMarker,
  onFavoriteClick,
  shortlist
}) => {
  const messagingModel = mapModel && mapModel.messagingModel;
  const sheetProps = {
    splitView,
    selectedHotel,
    selectedMarker,
    onFavoriteClick
  };
  const isPropertyClick = selectedHotel && selectedHotel.id !== -1;

  return (
    <>
      <MapSearch
        messageDismissed={searchResults.dismissMapMessage}
        showSearchButton={mapModel.showSearchButton}
        searchMessage={searchResults.error ? l10n.formatText('maps.viewer.searchError.text') : mapModel.searchMessage}
        searchInProgress={searchResults.isLoading}
        center={mapCenter}
        splitView={splitView}
      />
      {mapModel.markers && (
        <ErrorBoundary label="hsr.map.errorboundary">
          <UitkMap {...mapProps}>
            <ClusteringMarkers {...clusteringMarkersProps} shouldShowHeartIcon={true} />
          </UitkMap>
        </ErrorBoundary>
      )}
      {splitView && showSheet && <DynamicSheet {...sheetProps} />}
      {!splitView && showSheet && (
        <Viewport>
          <ViewSmall>
            <DynamicSheet {...sheetProps} />
          </ViewSmall>
          <ViewMedium>
            <AbacusExperiment name="PWA_SRP_Map_Card_Parity">
              <AbacusControl>
                <DynamicSheet {...sheetProps} />
              </AbacusControl>
              <AbacusVariant bucket={1}>
                <div className="uitk-grid">
                  {isPropertyClick && (
                    <Listing
                      className="listing map-listing-card uitk-cell all-cell-2-3"
                      model={selectedHotel}
                      mapListingView={true}
                      shortlist={shortlist}
                      onFavoriteClick={onFavoriteClick}
                    />
                  )}
                  {!isPropertyClick && <DynamicSheet {...sheetProps} />}
                </div>
              </AbacusVariant>
            </AbacusExperiment>
          </ViewMedium>
        </Viewport>
      )}
      {messagingModel && !showSheet && !splitView && <MessagingSheet {...{ messagingModel }} />}
    </>
  );
};

const MapSearchWithMarkers = inject('searchResults')(mapSearchWithMarkers);
